import { Autocomplete } from '@components/autocomplete';
import { filterVal, getData, matchData } from '@components/pageSection';
import type { AutoCompleteFundNodeType, AutoCompleteFundWithTrancheMergedType } from '@components/pageSection';
import type { FundsserviceAutocomplete_autocomplete$data } from '@generated/FundsserviceAutocomplete_autocomplete.graphql';
import { SearchIcon } from 'lucide-react';
import type * as React from 'react';
import { useFragment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { fundsserviceAutocompleteFragment } from './FundsserviceAutocomplete.relay';
import type { IFundsserviceAutocompleteProps } from './types';

const styleList: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  width: 'calc( 100% + 37px )',
  borderBottom: '1px solid #668272',
  boxShadow: '1px 1px 10px rgba( 0, 0, 0, .05 )',
  background: '#fff',
  overflow: 'auto',
  minHeight: '100px',
  marginTop: '30px',
};

const menuStyleNoEntries: React.CSSProperties = {
  textAlign: 'center',
  padding: '5px',
  fontSize: '14px',
  color: '#000000',
};

const dropdownStyle = {
  padding: '6px 12px',
  color: '#000000',
  fontSize: '14px',
  outline: 'none',
  textDecoration: 'none',
  cursor: 'pointer',
};

const dropdownHighlightedStyle = {
  ...dropdownStyle,
  backgroundColor: '#e0e0e0',
};

const FundsserviceOnChange = (
  value: string,
  autocompleteData: FundsserviceAutocomplete_autocomplete$data,
  salesAdmission: string,
  onInput: (newValue: string, newCount: number, onlyResultLink: string) => void,
  filterCountryFn: any,
) => {
  const filteredData = getData(autocompleteData.items.edges as AutoCompleteFundNodeType[])
    .filter(filterCountryFn)
    .filter(filterVal.bind(null, value));

  const resultCount = filteredData.length;

  let onlyResultLink = null;

  if (filteredData && resultCount === 1 && (value.toLowerCase() === filteredData[0].isin.toLowerCase() || value.toLowerCase() === filteredData[0].fundsname.toLowerCase())) {
    onlyResultLink = filteredData[0].url;
  }

  return onInput(value, resultCount, onlyResultLink);
};

const FundsserviceAutocomplete: React.FC<IFundsserviceAutocompleteProps> = (props: IFundsserviceAutocompleteProps) => {
  const navigate = useNavigate();

  const autocomplete = useFragment(fundsserviceAutocompleteFragment, props.autocomplete);

  const filterCountry = (tranche: any): boolean => {
    if (!props.salesAdmission || !tranche.salesadmission) {
      return false;
    }
    return tranche.salesadmission.indexOf(props.salesAdmission.toUpperCase()) > -1;
  };

  const getNewLocation = (pathname: string, liveFilterVal?: string) => {
    let locationDescriptor = { pathname: null, state: {} };

    if (typeof props.location === 'object') {
      locationDescriptor = {
        ...props.location,
        pathname,
      } as any;
    } else {
      locationDescriptor = {
        ...locationDescriptor,
        pathname,
      };
    }

    if (liveFilterVal) {
      locationDescriptor.state = {
        ...locationDescriptor.state,
        liveFilterInfo: {
          fundsname: [liveFilterVal],
          isin: [liveFilterVal],
        },
        salesAdmission: props.salesAdmission,
      };
    }

    return locationDescriptor;
  };

  return (
    <div className="FundsserviceAutocomplete relative z-499 inline-flex w-[280px] self-center justify-between items-center rounded-[25px] border border-[#c4c7c4] max-[1100px]:w-[310px] max-[640px]:mb-2.5 max-[640px]:w-full max-[640px]:[&>div]:w-full max-[640px]:[&_input]:w-full [&>div]:w-full [&_input]:w-full [&_input]:border-none [&_input]:bg-transparent [&_input]:px-3 [&_input]:py-[5px] [&_input]:text-sm [&_input]:font-normal [&_input]:text-accent [&_input:focus]:border-none [&_input:focus]:outline-none">
      <Autocomplete<AutoCompleteFundWithTrancheMergedType>
        value={props.value}
        placeholder={props.placeholder}
        onInput={(e, value) => {
          return FundsserviceOnChange(value, autocomplete, props.salesAdmission, props.onInput, filterCountry);
        }}
        onSelect={(value) => {
          const locationDescriptor = getNewLocation(value);
          navigate({ pathname: locationDescriptor.pathname }, { state: locationDescriptor.state });
        }}
        onUnselectedEnter={() => {
          props.onSearch();
        }}
        onFurtherSelect={() => {
          props.onSearch();
        }}
        items={getData(autocomplete.items.edges as AutoCompleteFundNodeType[]).filter(filterCountry)}
        getItemValue={(item) => item.url}
        getItemKey={(item) => `item${item.fundsname}${item.isin}`}
        shouldItemRender={matchData}
        styleList={styleList}
        renderItem={(item, isHighlighted) => (
          <div style={isHighlighted ? dropdownHighlightedStyle : dropdownStyle} className="FundsserviceAutocomplete-link">
            <strong>{item.fundsname}</strong>
            {` ( ${item.isin} )`}
          </div>
        )}
        renderFurtherMessageItem={(isHighlighted) => (
          <div style={isHighlighted ? dropdownHighlightedStyle : dropdownStyle} key={'further-results'} className="FundsserviceAutocomplete-link FundsserviceAutocomplete-further">
            {autocomplete.furtherResultsMessage}
          </div>
        )}
        renderNoResults={() => <div style={{ ...menuStyleNoEntries }}>{autocomplete.noResultsMessage}</div>}
        limit={5}
      />
      <SearchIcon className="text-accent hover:cursor-pointer mr-2.5" onClick={props.onSearch} />
    </div>
  );
};

export { FundsserviceAutocomplete };
